"use client";

import { useState, useCallback, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Image from "next/image";
import { CirclePlus, Unplug } from "lucide-react";

import { IntegrationsNCLModal } from "../../Modals/IntegrationsNCLModal";
import { useSpotUser } from "@/utils/spotUser";
import { EnvService } from "@/services/Environment/Environment";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { SpotToast } from "../../SpotToast";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { useStartIntegrationOAuth } from "./useStartIntegrationOAuth";
import { hasOAuthAppForm } from "./utils";
import type { IntegrationsInfoResponse } from "./types";
import { Button } from "../../shadcn/button";
import { Badge } from "../../shadcn/badge";
import { Skeleton } from "../../shadcn/skeleton";
import { cn } from "@/lib/utils";

interface ExternalLinkedAccountCardProps {
  slug: string;
  isUpdatingNotionPages?: boolean;
  onSuccess?: () => void;
  save?: () => Promise<void>;
  isDisabled?: boolean;
  returnTo?: string;
}

export const ExternalLinkedAccountCard = ({
  slug,
  isUpdatingNotionPages,
  onSuccess,
  save,
  isDisabled,
  returnTo: returnToProp,
}: ExternalLinkedAccountCardProps) => {
  const { spotGroup } = useSpotUser();
  const pathname = usePathname();
  const queryClient = useQueryClient();

  const { data: integrationsData, isLoading } =
    useQuery<IntegrationsInfoResponse>({
      queryKey: [
        DATA_KEYS.INTEGRATIONS_CONFIG,
        { path: { group_id: spotGroup.id } },
      ],
      queryFn: async () => {
        const resp = await axios.get(
          `/api/backend/groups/${spotGroup.id}/integrations/config`
        );
        return resp.data;
      },
      enabled: !!spotGroup?.id,
    });

  const [isNCLOpen, setIsNCLOpen] = useState(false);

  const integration = useMemo(
    () => integrationsData?.integrations?.find((i) => i.type === slug),
    [integrationsData, slug]
  );

  const isConnected = integration?.providers?.some((p) => p.connected) || false;

  const baseUrl = EnvService.getVar("FRONTEND_URL");

  const getReturnTo = useCallback(() => {
    if (returnToProp) return returnToProp;
    if (
      pathname?.includes("/flows") ||
      pathname?.includes("/assistant") ||
      pathname?.includes("/data-hub")
    ) {
      return `${baseUrl}${pathname}`;
    }
    return `${baseUrl}/manage-workspace?tab=linked-accounts`;
  }, [returnToProp, pathname, baseUrl]);

  const { start: startOAuth, adminSetupDialog } = useStartIntegrationOAuth({
    groupId: spotGroup.id,
    returnTo: getReturnTo(),
  });

  const handleConnect = useCallback(async () => {
    if (!integration) return;

    analytics_track(ANALYTICS_EVENTS.INTEGRATIONS_CONNECT_ACCOUNT, {
      integration_name: slug,
    });

    const availableProviders = integration.providers.filter((p) => p.available);

    // Single OAuth-only provider → start OAuth directly
    if (
      availableProviders.length === 1 &&
      availableProviders[0].auth_type !== "secret_auth" &&
      !hasOAuthAppForm(availableProviders[0])
    ) {
      startOAuth({ integration, provider: availableProviders[0] });
      return;
    }

    // Everything else (SecretAuth, multi-provider) → open the NCL modal with picker
    if (availableProviders.length > 0) {
      setIsNCLOpen(true);
      return;
    }

    SpotToast.error(
      `No connection providers available for ${integration.display_name}. Check Connections.`
    );
  }, [integration, slug, startOAuth]);

  if (isUpdatingNotionPages) {
    return (
      <Button
        onClick={handleConnect}
        variant="secondary"
        className="gap-2"
        disabled={isDisabled}
      >
        <CirclePlus size={16} />
        {isDisabled
          ? "Ask an admin to import other pages"
          : "Import other pages"}
      </Button>
    );
  }

  if (isLoading) return <Skeleton className="h-18 w-full" />;

  if (!integration) {
    return (
      <div className="p-4 text-sm text-muted-foreground rounded-md border border-border">
        Integration not found. Please contact support.
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        disabled={isDisabled}
        onClick={isDisabled ? undefined : handleConnect}
        className={cn(
          "flex items-center gap-4 p-3 w-full text-left rounded-xl border border-border bg-background transition-all min-w-60",
          isDisabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:border-foreground/30"
        )}
      >
        <div className="relative w-10 h-10 shrink-0">
          <Image
            fill
            alt={integration.display_name}
            src={integration.image}
            style={{ objectFit: "contain" }}
          />
        </div>
        <div className="flex flex-col gap-0.5 min-w-0">
          <h3 className="text-sm font-medium leading-snug">
            {integration.display_name}
          </h3>
          <p className="text-xs text-muted-foreground leading-snug">
            {isDisabled
              ? "Ask an admin to connect"
              : isConnected
                ? "Existing connections are invalid: click to reconnect"
                : "Click to connect"}
          </p>
        </div>
      </button>

      {/* Connect modal (with provider picker when multiple providers) */}
      <IntegrationsNCLModal
        isOpen={isNCLOpen}
        onClose={() => {
          setIsNCLOpen(false);
          queryClient.invalidateQueries({
            queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
          });
          queryClient.invalidateQueries({
            queryKey: [DATA_KEYS.INTEGRATIONS],
          });
          if (onSuccess) onSuccess();
        }}
        name={integration.display_name}
        integrationType={integration.type}
        image={integration.image}
        providers={integration.providers}
        configEndpoint=""
        submitEndpoint=""
        onOAuthSelect={(provider, connectionConfig) =>
          startOAuth({ integration, provider, connectionConfig })
        }
      />
      {adminSetupDialog}
    </>
  );
};
